import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaCar } from 'react-icons/fa';
import { ChevronRight } from 'lucide-react';
import { toast } from 'react-toastify';
import { discoveryService } from '../../services/apiService';
import { BUYER_SUB_SYSTEMS } from '../../constants/buyerSubSystems';
import { useVehicle } from '../../contexts/VehicleContext';
import { CategoryCardSkeleton } from '../Skeleton';

export default function SubSystemBrowse() {
  const navigate = useNavigate();
  const { selectedVehicle } = useVehicle();
  const vehicleId = selectedVehicle?._id || selectedVehicle?.id || '';

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await discoveryService.browseSubSystems(vehicleId || undefined);
      setData(res?.data || res);
    } catch (err) {
      toast.error(err.message || 'Could not load sub-systems');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    load();
  }, [load]);

  const requiresVehicle = data?.requiresVehicle;
  const countByName = useMemo(() => {
    const map = {};
    for (const sub of data?.subSystems || []) {
      const name = sub.name || sub.subSystem;
      if (name) map[name] = sub.partCount ?? sub.partCategoryCount;
    }
    return map;
  }, [data?.subSystems]);

  const vehicleQs = vehicleId ? `?vehicleId=${vehicleId}` : '';

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 mb-4"
        >
          <FaArrowLeft /> Back
        </button>

        <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">Browse by category</h1>
        <p className="text-sm text-slate-600 mb-6">
          24 sub-systems — tap a category to see filtered listings
        </p>

        {data?.vehicle && (
          <p className="text-xs font-semibold text-indigo-700 bg-indigo-50 rounded-lg px-3 py-2 mb-4 inline-block">
            {data.vehicle.make} {data.vehicle.model} {data.vehicle.year}
          </p>
        )}

        {requiresVehicle && (
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mb-6">
            <p className="text-sm text-amber-900 mb-3">{data.message}</p>
            <Link
              to="/account/vehicles"
              className="inline-flex items-center gap-2 rounded-full bg-amber-600 text-white px-4 py-2 text-sm font-semibold hover:bg-amber-700"
            >
              <FaCar /> Add vehicle
            </Link>
            <span className="mx-2 text-slate-400">or</span>
            <span className="text-sm text-slate-600">browse categories without fitment counts</span>
          </div>
        )}

        {loading ? (
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-4 px-4 snap-x snap-mandatory scrollbar-hide">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="snap-start shrink-0 w-[88px]">
                <CategoryCardSkeleton />
              </div>
            ))}
          </div>
        ) : (
          <div
            className="flex gap-3 overflow-x-auto pb-3 -mx-4 px-4 sm:mx-0 sm:px-0 snap-x snap-mandatory scrollbar-hide"
            role="list"
            aria-label="Browse categories"
          >
            {BUYER_SUB_SYSTEMS.map((cat) => {
              const Icon = cat.icon;
              const backendCount = cat.backendKeys.reduce(
                (sum, key) => sum + (Number(countByName[key]) || 0),
                0
              );
              const href = `/discovery/browse/${cat.slug}${vehicleQs}`;

              return (
                <Link
                  key={cat.slug}
                  to={href}
                  role="listitem"
                  className="group snap-start shrink-0 w-[88px] sm:w-[96px] flex flex-col items-center text-center"
                >
                  <div className="flex h-[72px] w-[72px] sm:h-20 sm:w-20 items-center justify-center rounded-2xl border border-slate-200 bg-white shadow-sm transition group-hover:border-indigo-400 group-hover:shadow-md group-hover:-translate-y-0.5">
                    <Icon className="h-8 w-8 text-indigo-600" strokeWidth={1.75} />
                  </div>
                  <span className="mt-2 text-[11px] sm:text-xs font-semibold text-slate-800 leading-tight line-clamp-2 px-0.5">
                    {cat.label}
                  </span>
                  {!requiresVehicle && backendCount > 0 && (
                    <span className="text-[10px] text-slate-500 mt-0.5">{backendCount}</span>
                  )}
                </Link>
              );
            })}
          </div>
        )}

        <div className="mt-8 rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-sm font-semibold text-slate-900 mb-2">Vehicle-specific part tree</p>
          <p className="text-xs text-slate-600 mb-3">
            Drill into catalog nodes by part category when your vehicle is saved.
          </p>
          <button
            type="button"
            onClick={() => navigate(`/discovery/subsystem/Engine${vehicleQs}`)}
            className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 hover:underline"
          >
            Open legacy category drill-down
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
